import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logoImage from '../../assets/logo.png';
import { guideRatingUpdater } from '../../utils/guideRatingUpdater';
import {
  ChartBarIcon,
  PlusIcon,
  EyeIcon,
  CogIcon,
  PhotoIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  StarIcon,
  AdjustmentsHorizontalIcon,
  DocumentTextIcon,
  UserGroupIcon,
  TagIcon,
  ShoppingCartIcon,
  ShoppingBagIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import GiftsAdmin from './GiftsAdmin';
import TourForm from '../../components/Admin/TourForm';
import TourList from '../../components/Admin/TourList';
import GuideManagement from '../../components/Admin/GuideManagement';
import { BookingManagement } from '../../components/Admin/BookingManagement';
import { LogisticsManagement } from '../../components/Admin/LogisticsManagement';
import FeaturedTourOrder from '../../components/Admin/FeaturedTourOrder';
import AboutManagement from '../../components/Admin/AboutManagement';
import CategoryManagement from '../../components/Admin/CategoryManagement';
import CustomSafariManagement from '../../components/Admin/CustomSafariManagement';
import AccommodationManagement from '../../components/Admin/AccommodationManagement';
import AdminRequests from './AdminRequests';
import { AdminSettings } from '../../components/Admin/AdminSettings';
import ScrollToTop from '../../components/Common/ScrollToTop';
import { tourService } from '../../services/tourService';
import { apiService } from '../../services/apiService';
import { Tour } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';
import { useCategoryOrder } from '../../contexts/CategoryOrderContext';
import { applyRoundRobinOrder } from '../../utils/applyRoundRobinOrder';
import { useToast } from '../../contexts/ToastContext';
import { usePricing } from '../../contexts/PricingContext';
import { currencyService } from '../../services/currencyService';

const AdminDashboard: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [tours, setTours] = useState<Tour[]>([]);
  const [stats, setStats] = useState({
    totalTours: 0,
    activeTours: 0,
    totalRevenue: 0,
    avgRating: 0
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabState] = useState(searchParams.get('tab') || 'overview');
  const [isAtTop, setIsAtTop] = useState(true);
  const [adminUser, setAdminUser] = useState<any>(null);
  const [adminPermissions, setAdminPermissions] = useState<string[]>([]);

  // Track scroll position for mobile footer visibility
  useEffect(() => {
    const handleScroll = () => setIsAtTop(window.scrollY < 80);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const setActiveTab = (tab: string) => {
    setActiveTabState(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTabState(tabFromUrl);
    }
  }, [searchParams]);
  const [showTourForm, setShowTourForm] = useState(false);
  const [editingTour, setEditingTour] = useState<Tour | undefined>(undefined);
  const navigate = useNavigate();
  const { actualTheme } = useTheme();
  const { featuredCategories } = useCategoryOrder();
  const { displayCurrency, tier, exchangeRates } = usePricing();

  useEffect(() => {
    // Check if user is authenticated
    const isAuthenticated = localStorage.getItem('adminAuth');
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    // Verify admin details and permissions with the backend
    const verifyAndLoad = async () => {
      try {
        const user = await apiService.getCurrentUser();
        if (!user || user.role !== 'admin' || user.admin_status !== 'approved') {
          showError('Access Denied', 'You do not have active administrator privileges.');
          localStorage.removeItem('adminAuth');
          navigate('/admin/login');
          return;
        }

        setAdminUser(user);
        setAdminPermissions(user.admin_permissions || []);

        // Load tours and stats from service - FORCE REFRESH to get latest data
        const [toursData, statsData] = await Promise.all([
          tourService.getAllTours(true), // Force refresh
          tourService.getTourStats()
        ]);
        
        let filteredToursData = toursData;
        const isSuperAdmin = user.email === 'sighttrailsafaris@gmail.com';
        
        if (!isSuperAdmin && user.admin_permissions) {
          const hasToursPerm = user.admin_permissions.includes('tours');
          const hasAllToursPerm = user.admin_permissions.includes('tours:all');
          const specificCategoryPerms = user.admin_permissions
            .filter((p: string) => p.startsWith('tours:') && p !== 'tours:all')
            .map((p: string) => p.split(':')[1]);

          if (hasToursPerm && !hasAllToursPerm && specificCategoryPerms.length > 0) {
            // Filter by specific category IDs (assuming categoryId is populated)
            // If not populated, we might need to rely on category service. For now filter by categoryId.
            filteredToursData = toursData.filter(tour => 
              tour.categoryId && specificCategoryPerms.includes(tour.categoryId)
            );
          }
        }

        const orderedTours = applyRoundRobinOrder(filteredToursData, featuredCategories);
        setTours(orderedTours);
        setStats(statsData);
      } catch (error) {
        console.error('Error loading admin profile:', error);
        showError('Session Expired', 'Please log in again.');
        localStorage.removeItem('adminAuth');
        navigate('/admin/login');
      }
    };

    verifyAndLoad();

    // Admin-only background tasks
    // guideRatingUpdater.startAutoUpdate(); // Disabled to prevent DB connection pool exhaustion

    return () => {
      guideRatingUpdater.stopAutoUpdate();
    }
  }, [navigate, featuredCategories]);

  // Tab guard: redirect to overview if trying to access unauthorized tab
  useEffect(() => {
    if (adminUser) {
      const isSuperAdmin = adminUser.email === 'sighttrailsafaris@gmail.com';
      const allowedTabs = [
        'overview',
        ...(isSuperAdmin ? ['admin-requests'] : []),
        ...adminPermissions
      ];
      if (!allowedTabs.includes(activeTab)) {
        setActiveTab('overview');
      }
    }
  }, [activeTab, adminUser, adminPermissions]);

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    apiService.logout();
    guideRatingUpdater.stopAutoUpdate();
    navigate('/admin/login');
  };
  const handleAddTour = () => {
    setEditingTour(undefined);
    setShowTourForm(true);
  };

  const handleEditTour = async (tour: any) => {
    try {
      console.log('Fetching full tour data for editing:', tour.id);
      // Fetch full tour data by ID to ensure we have JSON fields (highlights, itinerary, etc.)
      // which are stripped from the main listing for performance
      const fullTour = await tourService.getFullTourById(tour.id);
      if (fullTour) {
        setEditingTour(fullTour);
        setShowTourForm(true);
      } else {
        // Fallback to the listing data if fetch fails
        setEditingTour(tour);
        setShowTourForm(true);
      }
    } catch (error) {
      console.error('Error fetching full tour data:', error);
      setEditingTour(tour);
      setShowTourForm(true);
    }
  };

  const handleDeleteTour = async (tourId: string) => {
    if (window.confirm('Are you sure you want to delete this tour?')) {
      try {
        const success = await tourService.deleteTour(tourId);
        if (success) {
          const toursData = await tourService.getAllTours(true); // Force refresh
          setTours(applyRoundRobinOrder(toursData, featuredCategories));
        }
      } catch (error) {
        console.error('Error deleting tour:', error);
      }
    }
  };

  const handleBulkDeleteTours = async (tourIds: string[]) => {
    try {
      const deletePromises = tourIds.map(id => tourService.deleteTour(id));
      await Promise.all(deletePromises);
      // Force refresh after bulk delete
      const toursData = await tourService.getAllTours(true);
      setTours(applyRoundRobinOrder(toursData, featuredCategories));
      const statsData = await tourService.getTourStats();
      setStats(statsData);
      showSuccess('Success', 'Tours deleted successfully.');
    } catch (error) {
      console.error('Error deleting tours:', error);
      showError('Error', 'Error deleting some tours. Please try again.');
    }
  };

  const handleBulkUpdateStatus = async (tourIds: string[], status: 'active' | 'scheduled' | 'suspended' | 'deactivated' | 'expired') => {
    try {
      // Get all tours first, then update only the ones that need status change
      // Force refresh to get latest data
      const allTours = await tourService.getAllTours(true);
      const toursToUpdate = allTours.filter(tour => tourIds.includes(tour.id) && tour.status !== status);

      const togglePromises = tourIds.map(tourId => {
        return tourService.updateTour(tourId, { status });
      });

      await Promise.all(togglePromises);
      // Force refresh after bulk status update
      const toursData = await tourService.getAllTours(true);
      setTours(applyRoundRobinOrder(toursData, featuredCategories));
      const statsData = await tourService.getTourStats();
      setStats(statsData);
      showSuccess('Status Updated', `Tours successfully updated to ${status}.`);
    } catch (error) {
      console.error('Error updating tour status:', error);
      showError('Update Failed', 'Error updating tour status. Please try again.');
    }
  };

  const handleSaveTour = async (tourData: any) => {
    try {
      console.log('Saving tour:', tourData);

      if (editingTour) {
        // Update existing tour
        console.log('Updating existing tour:', editingTour.id);
        const updatedTour = await tourService.updateTour(editingTour.id, tourData);
        if (updatedTour) {
          console.log('Tour updated successfully:', updatedTour);
          
          // Update local state by merging the full updated tour
          // This ensures we keep JSON fields (highlights, etc.) in the UI state
          setTours(prevTours => prevTours.map(t => t.id === editingTour.id ? updatedTour : t));
          
          // Still refresh stats in background
          tourService.getTourStats().then(setStats).catch(console.error);
          
          showSuccess('Tour Updated', 'Tour was successfully updated.');
          setShowTourForm(false);
          setEditingTour(undefined);
        } else {
          console.error('Failed to update tour');
          showError('Update Error', 'Failed to update tour. Please try again.');
          return;
        }
      } else {
        // Add new tour
        console.log('Adding new tour');
        const newTour = await tourService.addTour(tourData);
        if (newTour) {
          console.log('Tour added successfully:', newTour);
          
          // Add to local state and re-apply round-robin logic
          setTours(prevTours => applyRoundRobinOrder([newTour, ...prevTours], featuredCategories));
          
          // Refresh stats
          tourService.getTourStats().then(setStats).catch(console.error);
          
          showSuccess('Tour Created', 'New tour was successfully created.');
          setShowTourForm(false);
        }
      }

      localStorage.removeItem('tour_form_cache');
      console.log('Tour form closed successfully');
    } catch (error) {
      console.error('Error saving tour:', error);
      showError('Save Failed', 'Failed to save tour. Please check the console for details.');
    }
  };

  const statsData = [
    {
      name: 'Total Tours',
      value: stats.totalTours,
      icon: PhotoIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      name: 'Active Tours',
      value: stats.activeTours,
      icon: EyeIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      name: 'Total Revenue',
      value: `$${(stats.totalRevenue || 0).toLocaleString()}`,
      icon: CurrencyDollarIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      name: 'Avg Rating',
      value: stats.avgRating,
      icon: StarIcon,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    }
  ];

  if (showTourForm) {
    return (
      <TourForm
        tour={editingTour}
        onSave={handleSaveTour}
        onCancel={() => {
          setShowTourForm(false);
          setEditingTour(undefined);
        }}
      />
    );
  }

  return (
    <div className={`min-h-screen transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
      }`}>
      {/* Header */}
      <div className={`backdrop-blur-md shadow-modern sticky top-0 z-50 transition-all duration-300 ${actualTheme === 'dark'
        ? 'bg-slate-900/95 border-b border-slate-700/30'
        : 'bg-white/95 border-b border-neutral-200/50'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="logo-container h-10 w-10 rounded-lg overflow-hidden mr-3 hover-magnify-sm">
                <img
                  src={logoImage}
                  alt="Maasai SightTrails Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    // If image fails, show text fallback
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent && !parent.querySelector('.logo-fallback')) {
                      const fallback = document.createElement('div');
                      fallback.className = 'logo-fallback bg-primary-600 text-white font-bold text-lg flex items-center justify-center w-full h-full rounded-lg';
                      fallback.textContent = 'MS';
                      parent.appendChild(fallback);
                    }
                  }}
                />
              </div>
              <div>
                <h1 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>Admin Dashboard</h1>
                <p className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                  }`}>Maasai SightTrails Safaris</p>
              </div>
            </div>
            {/* Header buttons - visible on desktop only */}
            <div className="hidden sm:flex items-center space-x-2 sm:space-x-4">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest border transition-all duration-300 hover:shadow-lg active:scale-95 ${actualTheme === 'dark'
                  ? 'text-slate-300 border-slate-700 bg-slate-800/50 hover:bg-slate-700'
                  : 'text-primary-700 border-primary-100 bg-primary-50 hover:bg-primary-100'
                  }`}
              >
                View Website
              </a>
              <button
                onClick={handleLogout}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all duration-300 hover:shadow-lg active:scale-95 ${actualTheme === 'dark'
                  ? 'bg-slate-700/50 text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                  : 'bg-neutral-100 text-gray-500 hover:text-gray-900 hover:bg-neutral-200'
                  }`}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs - Unified Responsive Horizontal Scroll */}
        <div id="admin-sidebar" className="mb-8 relative group">
          <nav className="flex overflow-x-scroll scrollbar-hide !flex-nowrap gap-2 sm:gap-3 pb-4 pt-2 px-1 relative z-10" style={{ flexWrap: 'nowrap' }}>
            {[
              { id: 'overview', name: 'Overview', icon: ChartBarIcon },
              { id: 'tours', name: 'Tours', icon: PhotoIcon },
              { id: 'categories', name: 'Categories', icon: TagIcon },
              { id: 'featured-order', name: 'Featured Order', icon: AdjustmentsHorizontalIcon },
              { id: 'guides', name: 'Guides', icon: StarIcon },
              { id: 'bookings', name: 'Bookings', icon: CalendarIcon },
              { id: 'custom-safaris', name: 'Custom Safaris', icon: ShoppingCartIcon },
              { id: 'accommodations', name: 'Accommodations', icon: BuildingOfficeIcon },
              { id: 'groups', name: 'Groups', icon: UserGroupIcon },
              { id: 'gifts', name: 'Gift Shop', icon: ShoppingBagIcon },
              { id: 'logistics', name: 'Logistics', icon: CurrencyDollarIcon },
              { id: 'about', name: 'About Page', icon: DocumentTextIcon },
              { id: 'settings', name: 'Settings', icon: CogIcon },
              ...(adminUser?.email === 'sighttrailsafaris@gmail.com' ? [{ id: 'admin-requests', name: 'Requests', icon: ShieldCheckIcon }] : [])
            ].filter(tab => 
              tab.id === 'overview' || 
              adminUser?.email === 'sighttrailsafaris@gmail.com' || 
              adminPermissions.includes(tab.id)
            ).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 flex-shrink-0 whitespace-nowrap shadow-sm hover:shadow-md hover:-translate-y-0.5 ${
                  activeTab === tab.id
                    ? actualTheme === 'dark'
                      ? 'bg-slate-700 text-slate-100 ring-2 ring-slate-600 ring-offset-2 ring-offset-slate-900'
                      : 'bg-primary-800 text-white ring-2 ring-primary-100 ring-offset-2 ring-offset-white'
                    : actualTheme === 'dark'
                      ? 'bg-slate-800/80 text-slate-400 border border-slate-700/50 hover:text-slate-200 hover:bg-slate-700/50'
                      : 'bg-white text-gray-600 border border-neutral-200 hover:text-primary-800 hover:border-primary-200 hover:bg-primary-50/50'
                }`}
              >
                <tab.icon className={`h-4 w-4 mr-2 ${activeTab === tab.id ? 'animate-pulse' : ''}`} />
                {tab.name}
              </button>
            ))}
          </nav>
          
          {/* Visual indicators for more items on small screens */}
          <div className={`absolute right-0 top-0 bottom-4 w-12 bg-gradient-to-l pointer-events-none md:hidden opacity-100 group-hover:opacity-0 transition-opacity duration-300 z-20 ${
            actualTheme === 'dark' ? 'from-slate-900 via-slate-900/80 to-transparent' : 'from-neutral-50 via-neutral-50/80 to-transparent'
          }`} />
          <div className={`absolute left-0 top-0 bottom-4 w-12 bg-gradient-to-r pointer-events-none md:hidden opacity-0 group-hover:opacity-40 transition-opacity duration-300 z-20 ${
            actualTheme === 'dark' ? 'from-slate-900 to-transparent' : 'from-neutral-50 to-transparent'
          }`} />
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {statsData.map((stat) => (
                <div key={stat.name} className={`rounded-2xl shadow-modern p-6 backdrop-blur-sm border transition-all duration-300 hover-magnify-sm ${actualTheme === 'dark'
              ? 'bg-slate-800/90 border-slate-700/30'
                  : 'bg-white/90 border-neutral-200/50'
                  }`}>
                  <div className="flex items-center">
                    <div className={`p-3 rounded-2xl ${stat.bgColor} transition-all duration-300`}>
                      <stat.icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                    <div className="ml-4">
                      <p className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                        }`}>{stat.name}</p>
                      <p className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                        }`}>{stat.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Tours */}
            <div className={`rounded-2xl shadow-modern backdrop-blur-sm border transition-all duration-300 ${actualTheme === 'dark'
              ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
              : 'bg-white/90 border-neutral-200/50'
              }`}>
              <div className={`px-6 py-4 border-b transition-colors duration-300 ${actualTheme === 'dark' ? 'border-slate-600/30' : 'border-gray-200'
                }`}>
                <h3 className={`text-lg font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>All Tours ({tours.length})</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Show ALL tours, original tours first, then new ones */}
                  {tours && tours.length > 0 ? tours.map((tour) => (
                    <div key={tour.id} className={`border rounded-2xl p-4 transition-all duration-300 hover-magnify-sm ${actualTheme === 'dark'
                      ? 'border-slate-600/30 bg-slate-800/50'
                      : 'border-gray-200 bg-white/50'
                      }`}>
                      <img
                        src={tour.images && tour.images.length > 0 ? tour.images[0] : 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'}
                        alt={tour.title}
                        className="w-full h-32 object-cover rounded-2xl mb-3"
                      />
                      <h4 className={`font-medium mb-1 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                        }`}>{tour.title}</h4>
                      <p className={`text-sm mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                        }`}>{tour.description}</p>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                          }`}>
                          {(() => {
                            // Use the normalized price as the starting point
                            let amount = (tour as any).price || 0;
                            
                            // If a specific tier is selected, try to get that specific tier price
                            if (tour.tierVariants && (tour.tierVariants as any)[tier]) {
                              const v = (tour.tierVariants as any)[tier];
                              // Check both possible structures: { international: { adult: X } } OR { baseServiceCosts: { INTERNATIONAL: { adult: X } } }
                              const local = v.local || v.baseServiceCosts?.LOCAL || v.baseServiceCosts?.local || {};
                              const international = v.international || v.baseServiceCosts?.INTERNATIONAL || v.baseServiceCosts?.international || {};
                              
                              const localAdult = local.adult || local.adultBase || 0;
                              const intlAdult = international.adult || international.adultBase || 0;
                              
                              if (localAdult > 0 || intlAdult > 0) {
                                // Default to local if available, otherwise international
                                amount = localAdult > 0 ? localAdult : intlAdult;
                              }
                            }

                            // If amount is still 0, try to find ANY price in variants
                            if (amount === 0 && tour.tierVariants) {
                              const findAny = (obj: any) => {
                                for (const k in obj) {
                                  const v = obj[k];
                                  const rates = v.local || v.international || v.baseServiceCosts?.LOCAL || v.baseServiceCosts?.local || v.baseServiceCosts?.INTERNATIONAL || {};
                                  if (rates.adult || rates.adultBase) return rates.adult || rates.adultBase;
                                }
                                return 0;
                              };
                              amount = findAny(tour.tierVariants);
                            }

                            // Convert if needed
                            let displayAmount = amount;
                            const isKes = displayCurrency === 'KES';
                            
                            if (isKes && amount < 1000 && exchangeRates?.KES) {
                              displayAmount = amount * exchangeRates.KES;
                            } else if (!isKes && amount > 1000 && exchangeRates?.KES) {
                              displayAmount = amount / exchangeRates.KES;
                            }

                            return (
                              <>
                                <span className="text-xs font-normal text-gray-500 mr-1 italic">Starts from</span>
                                {currencyService.formatCurrency(displayAmount, displayCurrency)} per adult
                              </>
                            );
                          })()}
                        </span>
                        <div className="flex items-center">
                          <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                          <span className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                            }`}>{tour.rating}</span>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className={`col-span-full text-center py-8 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                      }`}>
                      No tours available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tours Tab */}
        {activeTab === 'tours' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                }`}>Tour Management</h2>
              <button
                onClick={handleAddTour}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-modern shadow-modern text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-300 hover-magnify-sm modern-btn modern-focus"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add New Tour
              </button>
            </div>

            <TourList
              tours={tours}
              onEdit={handleEditTour}
              onDelete={handleDeleteTour}
              onBulkDelete={handleBulkDeleteTours}
              onBulkUpdateStatus={handleBulkUpdateStatus}
            />
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <CategoryManagement />
        )}

        {/* Featured Order Tab */}
        {activeTab === 'featured-order' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                }`}>Featured Category Order</h2>
            </div>

            <FeaturedTourOrder />
          </div>
        )}

        {/* Guides Tab */}
        {activeTab === 'guides' && (
          <GuideManagement />
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <BookingManagement />
        )}

        {/* Custom Safaris Tab */}
        {activeTab === 'custom-safaris' && (
          <CustomSafariManagement />
        )}

        {/* Accommodations Tab */}
        {activeTab === 'accommodations' && (
          <AccommodationManagement />
        )}

        {/* Groups Tab */}
        {activeTab === 'groups' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                }`}>Group Management</h2>
              <a
                href="/admin/groups"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-modern shadow-modern text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-300 hover-magnify-sm modern-btn modern-focus"
              >
                <UserGroupIcon className="h-4 w-4 mr-2" />
                Manage Groups
              </a>
            </div>

            <div className={`rounded-2xl shadow-modern backdrop-blur-sm border p-6 transition-all duration-300 ${actualTheme === 'dark'
              ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
              : 'bg-white/90 border-neutral-200/50'
              }`}>
              <div className="text-center py-8">
                <UserGroupIcon className="w-16 h-16 text-primary-500 mx-auto mb-4" />
                <h3 className={`text-xl font-semibold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>Group Portal Access</h3>
                <p className={`mb-6 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                  Access and manage all safari groups, communicate with clients, and monitor group activities.
                </p>
                <div className="space-y-4">
                  <a
                    href="/admin/groups"
                    className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-modern font-medium transition-all duration-300 hover-magnify-sm modern-btn modern-focus"
                  >
                    <UserGroupIcon className="w-5 h-5 mr-2" />
                    View All Groups
                  </a>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    • Monitor group activities and client interactions<br />
                    • Send group messages and announcements<br />
                    • Manage logistics and group assignments<br />
                    • One-on-one chat with individual clients
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Logistics Tab */}
        {activeTab === 'logistics' && (
          <LogisticsManagement />
        )}

        {/* About Page Tab */}
        {activeTab === 'about' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                }`}>About Page Management</h2>
            </div>

            <AboutManagement />
          </div>
        )}

        {/* Gifts Tab */}
        {activeTab === 'gifts' && (
          <GiftsAdmin />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <AdminSettings />
        )}

        {/* Requests Tab */}
        {activeTab === 'admin-requests' && adminUser?.email === 'sighttrailsafaris@gmail.com' && (
          <AdminRequests />
        )}
      </div>

      {/* Scroll to Top Button */}
      <ScrollToTop />

      {/* Mobile Footer - Website & Logout (visible only on mobile, shows when at top) */}
      <div className={`sm:hidden fixed bottom-0 left-0 right-0 z-40 transition-all duration-300 transform ${
        isAtTop ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
      }`}>
        <div className={`flex items-center justify-between gap-3 px-4 py-3 border-t backdrop-blur-md shadow-[0_-4px_20px_rgba(0,0,0,0.08)] ${
          actualTheme === 'dark'
            ? 'bg-slate-900/95 border-slate-700/50'
            : 'bg-white/95 border-neutral-200'
        }`}>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex-1 flex items-center justify-center py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all active:scale-95 ${
              actualTheme === 'dark'
                ? 'text-slate-300 border-slate-700 bg-slate-800/70'
                : 'text-primary-700 border-primary-100 bg-primary-50'
            }`}
          >
            🌐 View Website
          </a>
          <button
            onClick={handleLogout}
            className={`flex-1 flex items-center justify-center py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all active:scale-95 ${
              actualTheme === 'dark'
                ? 'text-red-400 border-red-900/50 bg-red-950/30'
                : 'text-red-600 border-red-100 bg-red-50'
            }`}
          >
            🚪 Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
